import { useMemo, useState, useEffect, useRef } from 'react';
import { Globe, Building2, Info, X } from 'lucide-react';
import { Card } from './ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { fmtValueParts } from '../lib/format';
import type { CountryData } from '../charts/AfricaMap';
import { getCompareColor } from '../charts/palette';
import { t } from '../i18n';

interface CountryCompareProps {
  selectedCountries: string[];
  countriesData: Map<string, CountryData>;
  units: string | null;
  unit?: string | null;
  description?: string | null;
  formula?: string | null;
  inverted?: boolean;
  indicator: string;
  africaMedian: number | null;
  countryColors: Record<string, string>;
  onRemoveCountry?: (country: string) => void;
  onClearAll?: () => void;
}

export function CountryCompare({
  selectedCountries,
  countriesData,
  units,
  unit,
  description,
  formula,
  inverted = false,
  indicator,
  africaMedian,
  countryColors,
  onRemoveCountry,
  onClearAll,
}: CountryCompareProps) {
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);

  // Close info tooltip on touch/click outside (specifically for mobile browsers)
  useEffect(() => {
    if (!isInfoOpen) return;
    const onDocPointerDown = (e: MouseEvent | TouchEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setIsInfoOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [isInfoOpen]);

  // Selected country data items
  const selectedData = useMemo(() => {
    return selectedCountries.map((c) => ({
      country: c,
      color: countryColors[c] || getCompareColor(0),
      data: countriesData.get(c),
    }));
  }, [selectedCountries, countryColors, countriesData]);

  // Calculate comparative bar scales
  const barMetrics = useMemo(() => {
    const medians: number[] = [];
    if (africaMedian !== null && Number.isFinite(africaMedian)) {
      medians.push(africaMedian);
    }
    selectedData.forEach((item) => {
      if (item.data && Number.isFinite(item.data.median)) {
        medians.push(item.data.median);
      }
    });

    if (medians.length === 0) return { maxVal: 1 };
    const maxVal = Math.max(...medians, 0.0001);
    return { maxVal };
  }, [selectedData, africaMedian]);

  return (
    <Card className="h-full flex-1 flex flex-col border-hair bg-glass overflow-hidden shadow-none min-h-0">
      {/* Header Bar: Full Indicator Name, Info Tooltip with description & formula, and Polarity Badge */}
      <div className="px-4 py-3 border-b border-hair bg-glass-2 flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-2 min-w-0" ref={infoRef}>
          <span className="font-serif font-bold text-sm text-txt leading-snug truncate" title={indicator}>
            {indicator}
          </span>
          {(description || formula) && (
            <TooltipProvider delayDuration={150}>
              <Tooltip open={isInfoOpen} onOpenChange={setIsInfoOpen}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsInfoOpen((v) => !v);
                    }}
                    onMouseEnter={() => setIsInfoOpen(true)}
                    onMouseLeave={() => setIsInfoOpen(false)}
                    className="text-dim hover:text-txt transition-colors shrink-0 p-1 rounded focus:outline-none focus-visible:ring-1 focus-visible:ring-gold"
                    aria-label="Indicator information"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="max-w-[calc(100vw-32px)] sm:max-w-sm p-3.5 space-y-2 bg-white dark:bg-neutral-900/95 border border-slate-200 dark:border-hair text-slate-900 dark:text-txt shadow-2xl rounded-lg backdrop-blur-md z-50 pointer-events-auto"
                  onPointerDownOutside={() => setIsInfoOpen(false)}
                >
                  <div className="flex items-start justify-between gap-2 pb-1 border-b border-slate-200 dark:border-hair/60">
                    <span className="font-bold text-xs text-slate-900 dark:text-txt leading-snug">
                      {indicator}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsInfoOpen(false);
                      }}
                      className="text-slate-400 hover:text-slate-700 dark:hover:text-txt p-0.5 rounded -mr-1"
                      aria-label="Close"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {description && (
                    <p className="text-xs text-slate-600 dark:text-neutral-300 leading-relaxed font-sans font-normal">
                      {description}
                    </p>
                  )}
                  {formula && (
                    <div className="text-[10px] font-mono text-slate-700 dark:text-neutral-400 bg-slate-100 dark:bg-neutral-950/80 p-2 rounded border border-slate-200 dark:border-hair/60 break-words leading-tight">
                      <span className="font-semibold text-slate-900 dark:text-neutral-200 uppercase tracking-wider text-[9px] block mb-1">
                        {t.formula}:
                      </span>
                      {formula}
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Polarity Badge (standardized with radar plot hover style) */}
        {inverted ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
            ↓ {t.mapBetter(true)}
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
            ↑ {t.mapBetter(false)}
          </span>
        )}
      </div>

      {selectedCountries.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-10 h-10 rounded-full bg-glass-3 border border-hair flex items-center justify-center text-gold mb-3">
            <Globe className="w-5 h-5 opacity-80" />
          </div>
          <h3 className="text-sm font-semibold mb-1 text-txt">
            {t.compareNoSelection}
          </h3>
          <p className="max-w-xs text-xs text-dim leading-relaxed">
            Click countries on the Africa map or use the sidebar filters to select up to 4 countries.
          </p>
        </div>
      ) : (
        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          {/* Comparative Bar Chart vs Africa Benchmark */}
          <div className="space-y-2">
            <div className="space-y-2">
              {/* Africa Benchmark Bar */}
              {africaMedian !== null && Number.isFinite(africaMedian) && (
                <div className="space-y-0.5">
                  <div className="flex items-center justify-between text-[10.5px]">
                    <span className="font-mono text-dim font-medium">
                      {t.africaBenchmark}
                    </span>
                    <span className="font-mono font-semibold text-txt">
                      {fmtValueParts(units, africaMedian, unit).text}{' '}
                      <span className="text-[9.5px] text-mut">{fmtValueParts(units, africaMedian, unit).unit}</span>
                    </span>
                  </div>
                  <div className="w-full h-1.5 rounded bg-glass-3 overflow-hidden border border-hair/40">
                    <div
                      className="h-full bg-neutral-400 dark:bg-neutral-600 rounded"
                      style={{
                        width: `${Math.min(100, Math.max(3, (africaMedian / barMetrics.maxVal) * 100))}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Selected Countries Bars */}
              {selectedData.map(({ country, color, data }) => {
                if (!data) {
                  return (
                    <div key={country} className="space-y-0.5 opacity-60">
                      <div className="flex items-center justify-between text-[10.5px]">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                          <span className="font-semibold text-txt">{country}</span>
                          <span className="text-[9px] px-1 py-0 rounded font-medium text-dim bg-glass-2 border border-hair/40">
                            {t.noDataCard}
                          </span>
                        </div>
                        <span className="font-mono text-dim text-[10px]">{t.noReportingUtilities}</span>
                      </div>
                      <div className="w-full h-1.5 rounded bg-glass-3 border border-hair/20" />
                    </div>
                  );
                }
                const val = data.median;
                const pctOfMax = Math.min(100, Math.max(3, (val / barMetrics.maxVal) * 100));
                const fVal = fmtValueParts(units, val, unit);

                const isBetter =
                  africaMedian !== null
                    ? inverted
                      ? val < africaMedian
                      : val > africaMedian
                    : null;

                return (
                  <div key={country} className="space-y-0.5">
                    <div className="flex items-center justify-between text-[10.5px]">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        <span className="font-semibold text-txt">{country}</span>
                        {isBetter !== null && (
                          <span
                            className={`text-[9px] px-1 py-0 rounded font-medium ${
                              isBetter
                                ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/15'
                                : 'text-amber-700 dark:text-amber-300 bg-amber-500/15'
                            }`}
                          >
                            {isBetter ? t.badgeBetter : t.badgeBelow}
                          </span>
                        )}
                      </div>
                      <span className="font-mono font-bold text-txt">
                        {fVal.text} <span className="text-[9.5px] text-mut">{fVal.unit}</span>
                      </span>
                    </div>

                    <div className="w-full h-1.5 rounded bg-glass-3 overflow-hidden border border-hair/40">
                      <div
                        className="h-full rounded transition-all duration-300"
                        style={{
                          width: `${pctOfMax}%`,
                          backgroundColor: color,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Minimalist Dossiers: Clean columns with subtle separators, NO loud borders */}
          <div className="space-y-3">
            <div className="text-[11px] font-semibold text-mut uppercase tracking-wider">
              {t.utilityBreakdown}
            </div>

            <div
              className={`grid gap-3 ${
                selectedData.length === 1
                  ? 'grid-cols-1'
                  : selectedData.length === 2
                  ? 'grid-cols-1 sm:grid-cols-2'
                  : 'grid-cols-1 sm:grid-cols-2'
              }`}
            >
              {selectedData.map(({ country, color, data }) => {
                if (!data || data.utilities.length === 0) {
                  return (
                    <div
                      key={country}
                      className="rounded-md border border-hair/80 bg-glass-2 p-3 space-y-2.5"
                    >
                      <div className="flex items-center justify-between pb-1.5 border-b border-hair/50">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                          <span className="font-serif font-bold text-sm text-txt">{country}</span>
                        </div>
                        <span className="text-[10px] text-dim font-mono">{t.noDataCard}</span>
                      </div>

                      <div className="py-6 px-2 text-center text-xs text-dim">
                        {t.noObservationsForYear}
                      </div>
                    </div>
                  );
                }
                const iso = data.iso3c;
                const fMed = fmtValueParts(units, data.median, unit);
                const fMin = fmtValueParts(units, data.min, unit);
                const fMax = fmtValueParts(units, data.max, unit);

                return (
                  <div
                    key={country}
                    className="rounded-md border border-hair/80 bg-glass-2 p-3 space-y-2.5"
                  >
                    {/* Clean Minimal Header without utilities count badge */}
                    <div className="flex items-center justify-between pb-1.5 border-b border-hair/50">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                        <span className="font-serif font-bold text-sm text-txt">{country}</span>
                        {iso && (
                          <span className="font-mono text-[10px] text-dim uppercase">
                            ({iso})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Median & Delta metrics */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="text-[10px] text-dim">{t.nationalMedian}</div>
                        <div className="font-serif font-bold text-txt text-base">
                          {fMed.text} <span className="text-[10px] text-mut">{fMed.unit}</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-dim">{t.spread}</div>
                        <div className="font-mono text-txt text-xs pt-0.5">
                          {data.utilities.length > 1 ? (
                            <span>{fMin.text} – {fMax.text}</span>
                          ) : (
                            <span className="text-dim">{t.oneObservation}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Utilities List */}
                    <div className="space-y-1">
                      <div className="text-[10px] font-medium text-dim uppercase tracking-wider flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-gold" />
                        {t.memberUtilities}
                      </div>
                      <div className="max-h-28 overflow-y-auto space-y-1 pr-0.5">
                        {data.utilities.map((u) => {
                          const uFmt = fmtValueParts(units, u.value, unit);
                          return (
                            <div
                              key={u.id}
                              className="flex items-center justify-between text-[11px] py-0.5 px-1.5 rounded hover:bg-glass"
                            >
                              <span className="truncate pr-1 text-txt" title={u.name}>
                                {u.name}
                              </span>
                              <span className="font-mono font-semibold text-txt shrink-0">
                                {uFmt.text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
